import React, { useEffect, useState } from "react"
import { Card, CardBody, Spinner, UncontrolledTooltip } from "reactstrap"
import { Input, Button, Row, Col } from "reactstrap"
import toastr from "toastr"
import { ptBR } from "date-fns/locale"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import {
  FaSearch,
  FaCalendarAlt,
  FaList,
  FaTimes,
  FaInfoCircle,
} from "react-icons/fa"
import { getMonitored } from "helpers/blopsleads_helper"
import StatusMap, { getStatusTexFromValue } from "helpers/statusMap"
import { useSelector, useDispatch } from "react-redux"
import ConfirmModal from "components/Common/ConfirmModal"
import {
  sendLead as onSendLead,
  updateLead as onUpdateLead,
  updatePublicacao as onUpdatePublicacao,
  updateProcesso as onUpdateProcesso,
} from "store/actions"
import { Pagination } from "./PaginationComponent"
import { ProcessTable } from "./ProcessTable"
import "./Filters.css"
import formatNumber from "../Qualificacao/dashboad-process-components/formatNumber"
import { exportToExcel } from "helpers/exportToExcel"

const FiltersMonitor = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [searchCompany, setSearchCompany] = useState("")
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [selectedDateType, setSelectedDateType] = useState("")
  const [selectedStatus, setSelectedStatus] = useState("")
  const [filteredProcesses, setFilteredProcesses] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [confirmModal, setConfirmModal] = useState(false)
  const [customConfirmModal, setCustomConfirmModal] = useState({
    Evento: "",
    Message: "",
  })
  const [selectedProcess, setSelectedProcess] = useState(null)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [loadedBlocks, setLoadedBlocks] = useState({})
  const [filteredBlocks, setFilteredBlocks] = useState({})
  const [isFiltering, setIsFiltering] = useState(false)
  const [isFilteringCompany, setIsFilteringCompany] = useState(false)
  const [initialDataLoaded, setInitialDataLoaded] = useState(false)
  const [initialState, setInitialState] = useState({
    totalPages: 1,
    totalCount: 0,
    blocks: {},
    processes: [],
  })

  const dispatch = useDispatch()

  const itemsPerPage = 10
  const itemsPerRequest = 70000

  const hasActiveFilters = () => {
    return Boolean(
      searchTerm.trim() ||
        startDate ||
        endDate ||
        selectedStatus ||
        selectedDateType
    )
  }

  const fetchBlock = async (blockNumber, filters = {}) => {
    try {
      setLoading(true)
      const response = await getMonitored(blockNumber, itemsPerRequest, filters)

      if (Object.keys(filters).length > 0) {
        setFilteredBlocks(prev => ({
          ...prev,
          [blockNumber]: response.processes,
        }))
        setFilteredProcesses(response.processes)
        setTotalPages(Math.ceil(response.totalCount / itemsPerPage))
        setTotalCount(response.totalCount)
      } else {
        const newBlocks = {
          ...loadedBlocks,
          [blockNumber]: response.processes,
        }
        setLoadedBlocks(newBlocks)

        setTotalCount(response.totalCount)

        const totalPagesCount = Math.ceil(response.totalCount / itemsPerPage)
        setTotalPages(totalPagesCount)

        if (!initialDataLoaded) {
          setInitialState({
            totalPages: totalPagesCount,
            totalCount: response.totalCount,
            blocks: newBlocks,
            processes: response.processes,
          })
        }
      }
    } catch (err) {
      console.error("Failed to fetch monitored processes:", err)
    } finally {
      setLoading(false)
      setInitialDataLoaded(true)
    }
  }

  const getPaginatedData = () => {
    const currentBlock = Math.ceil(
      currentPage / (itemsPerRequest / itemsPerPage)
    )
    const blockStartIndex =
      ((currentPage - 1) % (itemsPerRequest / itemsPerPage)) * itemsPerPage

    if (isFiltering) {
      const blockProcesses = filteredBlocks[currentBlock] || []
      return blockProcesses.slice(
        blockStartIndex,
        blockStartIndex + itemsPerPage
      )
    } else if (isFilteringCompany) {
      return filteredProcesses.slice(
        blockStartIndex,
        blockStartIndex + itemsPerPage
      )
    } else {
      const blockProcesses = loadedBlocks[currentBlock] || []
      return blockProcesses.slice(
        blockStartIndex,
        blockStartIndex + itemsPerPage
      )
    }
  }

  useEffect(() => {
    if (!initialDataLoaded) {
      fetchBlock(1)
    }
  }, [])

  useEffect(() => {
    const currentBlock = Math.ceil(
      currentPage / (itemsPerRequest / itemsPerPage)
    )

    if (isFiltering && !isFilteringCompany) {
      if (!filteredBlocks[currentBlock]) {
        const filters = {
          searchTerm,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          status: selectedStatus,
          dateType: selectedDateType,
        }
        fetchBlock(currentBlock, filters)
      }
    } else if (!isFiltering && !isFilteringCompany) {
      if (!loadedBlocks[currentBlock] && initialDataLoaded) {
        fetchBlock(currentBlock)
      }
    }
  }, [
    currentPage,
    isFiltering,
    isFilteringCompany,
    filteredBlocks,
    loadedBlocks,
  ])

  const handleSearch = () => {
    if (!hasActiveFilters()) {
      return
    }

    if (searchTerm) {
      setStartDate(null)
      setEndDate(null)
      setSelectedStatus("")
      setSelectedDateType("")
    }

    setIsFiltering(true)
    setCurrentPage(1)
    setFilteredBlocks({})

    const filters = {
      searchTerm,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      status: selectedStatus,
      dateType: selectedDateType,
    }
    fetchBlock(1, filters)
  }

  const handleClearFilters = () => {
    // Limpa os inputs
    setSearchTerm("")
    setStartDate(null)
    setEndDate(null)
    setSelectedStatus("")
    setSelectedDateType("")

    setIsFiltering(false)
    setTotalPages(initialState.totalPages)
    setLoadedBlocks(initialState.blocks)
    setTotalCount(initialState.totalCount)
    setFilteredBlocks({})
    setCurrentPage(1)
  }

  const handleSearchWithValidation = () => {
    if (searchTerm) {
      handleSearch()
      return
    }

    const isInvalidDateRange =
      startDate && endDate && new Date(startDate) > new Date(endDate)
    const isMissingDateType =
      ((selectedDateType === "" && selectedStatus !== "") ||
        !startDate ||
        !endDate) &&
      !selectedStatus
    const isMissingDateRange =
      selectedDateType !== "" &&
      selectedStatus !== "" &&
      (!startDate || !endDate)
    const isPartialDateRange = !!startDate !== !!endDate

    const isMissingDateTypeWhenDatesProvided =
      startDate && endDate && selectedDateType === ""

    if (isInvalidDateRange) {
      toastr.error("A data inicial deve ser menor ou igual à data final.")
    } else if (isMissingDateTypeWhenDatesProvided) {
      toastr.error("Selecione um tipo de data!")
    } else if (isMissingDateType) {
      toastr.error("Selecione um tipo de data, a data de início e de fim!")
    } else if (isMissingDateRange) {
      toastr.error("Selecione um período de data!")
    } else if (isPartialDateRange) {
      toastr.error("Selecione ambas as datas!")
    } else {
      handleSearch()
    }
  }

  const paginatedProcesses = getPaginatedData()

  const getItemRange = (currentPage, itemsPerPage, totalCount) => {
    const startItem = (currentPage - 1) * itemsPerPage + 1
    const endItem = Math.min(currentPage * itemsPerPage, totalCount)
    return `${formatNumber(startItem)} - ${formatNumber(endItem)}`
  }

  const isSearchButtonEnabled = hasActiveFilters()

  const handleSendLead = process => {
    setCustomConfirmModal({
      Evento: "EnviarLead",
      Message:
        "Os dados do lead serão enviados para o cliente Pottencial. Deseja prosseguir?",
    })
    setSelectedProcess(process)
    setConfirmModal(true)
  }

  const handleStopMonitor = process => {
    setCustomConfirmModal({
      Evento: "PararMonitoramento",
      Message: "Você realmente deseja parar o monitoramento?",
    })
    setSelectedProcess(process)
    setConfirmModal(true)
  }


  const sendPottencialLead = processo => {
    if (processo?.Lead?.Status === "Enviado") {
        toastr.warning("Este lead já foi enviado.");
        return;
      }
    const idOk = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(processo?.Lead?.Id || "")
    if (!idOk) { toastr.error("Salve o lead antes de enviar (ID inválido)."); return }

    const updatedLead = { Id: processo.Lead.Id, DataEncerramento: null, Status: "Completo" }
    const payload = { ...processo.Lead, Destino: (processo.Lead?.Destino || "pottencial") }
    dispatch(onSendLead(payload))
    dispatch(onUpdateLead(updatedLead))
    dispatch(onUpdateProcesso({ Id: processo.Id, Monitored: true, IsLead: true }))
  }


  const cancelMonitor = processo => {
    dispatch(
      onUpdateProcesso({
        Id: processo.Id,
        Monitored: false,
        // IsLead: false,
        MotivoNotLead: "Outros",
      })
    )

    const { ProcessoRelacionado, ...leadSemProcessoRelacionado } = processo.Lead
    const updatedLead = {
      ...leadSemProcessoRelacionado,
      DataEncerramento: new Date(),
      Status: "Excluído",
    }

    dispatch(onUpdateLead(updatedLead))

    const updatedPublicacao = {
      Id: processo.Publicacoes[0].Id,
      Status: StatusMap.Excluido.value,
    }
    dispatch(onUpdatePublicacao(updatedPublicacao))
  }

  const handleConfirmModal = () => {
    if (customConfirmModal.Evento === "EnviarLead") {
      sendPottencialLead(selectedProcess)
    }
    if (customConfirmModal.Evento === "PararMonitoramento") {
      cancelMonitor(selectedProcess)
    }
    setConfirmModal(false)
  }

  const getStatusClass = status => {
    const StatusClassMap = {
      Novo: "badge bg-primary",
      "Em Aprovação": "badge bg-warning",
      Incompleto: "badge bg-danger",
      Concluído: "badge bg-success",
      Manual: "badge bg-info",
      Monitorado: "badge bg-dark",
      "Não Monitorado": "badge bg-secondary",
      "Não Priorizado": "badge bg-light text-dark",
      Excluído: "badge bg-danger text-white",
    }

    return StatusClassMap[status] || "badge bg-secondary"
  }

  const handleExportToExcel = () => {
    const dataToExport =
      filteredProcesses.length > 0 ? filteredProcesses : initialState.processes

    exportToExcel(dataToExport)
  }

  const handleSearchCompany = value => {
    setSearchCompany(value)
    setIsFiltering(false)
    setFilteredProcesses([])
    setIsFilteringCompany(false)

    setEndDate(null)
    setStartDate(null)
    setSelectedDateType("")
    setSelectedStatus("")

    if (value.trim() === "") {
      setTotalCount(initialState.totalCount)
      setTotalPages(Math.ceil(initialState.totalCount / itemsPerPage))
      return
    }

    const filtered = initialState.processes.filter(processo =>
      processo.Lead?.ReuRazaoSocial?.toLowerCase().includes(value.toLowerCase())
    )

    setFilteredProcesses(filtered)
    setIsFilteringCompany(true)
    setTotalCount(filtered.length)
    setTotalPages(Math.ceil(filtered.length / itemsPerPage))
  }

  return (
    <React.Fragment>
      <>
        <Card className="p-0 m-0">
          <CardBody>
            <div className="modern-filters-container">
              <Row className="g-3">
                {/* CNJ Search */}
                <Col lg="4" md="6">
                  <div className="modern-input-group">
                    <span className="modern-input-icon">
                      <FaList />
                    </span>
                    <Input
                      type="textarea"
                      placeholder="Buscar por CNJs (um por linha)"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="modern-textarea"
                      rows="5"
                    />
                  </div>
                </Col>

                <Col lg="3" md="6">
                  <div className="date-filters-container">
                    <div className="modern-input-group mb-2">
                      <select
                        className="modern-select"
                        value={selectedDateType}
                        onChange={e => setSelectedDateType(e.target.value)}
                        id="dateTypeSelect"
                      >
                        <option value="" disabled>
                          Selecione o tipo de data
                        </option>
                        <option value="movimentacoes">
                          Data de Movimentações
                        </option>
                        <option value="analise">Data de Análise</option>
                        <option value="monitoramento">
                          Data de Monitoramento
                        </option>
                        <option value="encerramento">
                          Data de Encerramento
                        </option>
                        <option value="publicacoes">Data de Publicações</option>
                      </select>
                      <span className="select-info-icon" id="dateTypeTooltip">
                        <FaInfoCircle />
                      </span>
                      <UncontrolledTooltip
                        placement="right"
                        target="dateTypeTooltip"
                      >
                        Selecione o tipo de data ao definir um período
                      </UncontrolledTooltip>
                    </div>

                    <div className="modern-input-group mb-1">
                      <span className="modern-input-icon">
                        <FaCalendarAlt />
                      </span>
                      <div className="modern-datepicker-wrapper">
                        <DatePicker
                          selected={startDate}
                          onChange={date => setStartDate(date)}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="Data de Início"
                          locale={ptBR}
                          className="modern-datepicker"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          yearDropdownItemNumber={15}
                          scrollableYearDropdown
                          isClearable
                          portalId="root-portal"
                        />
                      </div>
                    </div>
                    <div className="modern-input-group">
                      <span className="modern-input-icon">
                        <FaCalendarAlt />
                      </span>
                      <div className="modern-datepicker-wrapper">
                        <DatePicker
                          selected={endDate}
                          onChange={date => setEndDate(date)}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="Data de Fim"
                          locale={ptBR}
                          className="modern-datepicker"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          yearDropdownItemNumber={15}
                          scrollableYearDropdown
                          isClearable
                          portalId="root-portal"
                        />
                      </div>
                    </div>
                  </div>
                </Col>

                <Col lg="2" md="6">
                  <div className="modern-input-group">
                    <select
                      className="modern-select"
                      value={selectedStatus}
                      onChange={e => setSelectedStatus(e.target.value)}
                    >
                      <option value="" disabled hidden>
                        Status
                      </option>
                      {Object.keys(StatusMap).map(key => (
                        <option key={key} value={StatusMap[key].value}>
                          {StatusMap[key].text}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>

                <Col
                  lg="3"
                  md="12"
                  className="d-flex flex-column justify-content-between"
                >
                  <div className="search-filter-container">
                    <div className="input-container">
                      <Input
                        type="text"
                        placeholder="Buscar por Empresas"
                        value={searchCompany}
                        onChange={e => handleSearchCompany(e.target.value)}
                        className={`modern-text ${
                          searchCompany ? "has-clear-button" : ""
                        }`}
                      />
                      {searchCompany && (
                        <button
                          className="clear-input-button"
                          onClick={() => handleSearchCompany("")}
                          type="button"
                          aria-label="Limpar texto"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>

                    <div className="modern-button-group justify-content-between w-100">
                      <button
                        className="modern-button clear-button flex-grow-1"
                        onClick={handleClearFilters}
                        disabled={!hasActiveFilters()}
                      >
                        <FaTimes />
                        <span>Limpar</span>
                      </button>

                      <button
                        className="modern-button search-button flex-grow-1"
                        onClick={handleSearchWithValidation}
                        disabled={!isSearchButtonEnabled}
                      >
                        <FaSearch />
                        <span>Buscar</span>
                      </button>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "80vh" }}
              >
                <Spinner
                  color="primary"
                  style={{
                    width: "3rem",
                    height: "3rem",
                  }}
                />
              </div>
            ) : (
              <div>
                <div className="pagination-info">
                  <div style={{ alignSelf: "center" }}>
                    <p className="range">
                      {getItemRange(currentPage, itemsPerPage, totalCount)} de{" "}
                      <span className="total">{formatNumber(totalCount)}</span>
                    </p>
                  </div>
                  <div>
                    <Button
                      color="success"
                      onClick={handleExportToExcel}
                      id="excelTooltip"
                    >
                      Exportar Excel
                    </Button>
                    <UncontrolledTooltip placement="top" target="excelTooltip">
                      Exporte os itens filtrados
                    </UncontrolledTooltip>
                  </div>
                </div>

                {filteredProcesses.length === 0 &&
                paginatedProcesses.length === 0 ? (
                  <div className="text-center mt-4">
                    <p>Nenhum dado encontrado para os critérios de busca.</p>
                  </div>
                ) : (
                  <ProcessTable
                    paginatedProcesses={paginatedProcesses}
                    handleStopMonitor={handleStopMonitor}
                    handleSendLead={handleSendLead}
                    getStatusClass={getStatusClass}
                    getStatusTexFromValue={getStatusTexFromValue}
                  />
                )}

                <div className="pagination d-flex justify-content-center align-items-center mt-3 gap-3">
                  <Pagination
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    totalPages={totalPages}
                  />
                </div>
              </div>
            )}
          </CardBody>
        </Card>
        <ConfirmModal
          message={customConfirmModal.Message}
          show={confirmModal}
          onConfirmClick={handleConfirmModal}
          onCloseClick={() => setConfirmModal(false)}
        />
      </>
    </React.Fragment>
  )
}

export default FiltersMonitor
